import { computed, markRaw, nextTick, ref, shallowRef } from 'vue'

import { buildMediaDimensionFields, getMediaDimensionsFromFile, type RawMediaDimensions } from '../utils/dimensions.utils'

export interface DRMUploadConfig {
  /** App source identifier (e.g., 'communities', 'courses') */
  appSource: string
  /** Higher level entity type that owns the asset */
  entityType: string
  /** Unique identifier for the entity that will own the asset */
  entityId: string
  /** Lower level entity type that the asset is associated with */
  sourceEntityType: string
  /** Unique identifier for the source entity */
  sourceEntityId: string
}

export interface DRMUploadResult {
  /** License ID for the uploaded asset */
  licenseId: string
  /** Processed URL for display */
  url?: string
  /** Original URL */
  processedUrl?: string
  /** Asset type */
  assetType?: string
  /** Upload status */
  status?: string
  /** Creation timestamp */
  createdAt?: string
  /** Update timestamp */
  updatedAt?: string
  /**
   * Intrinsic media width measured client-side during upload.
   * Omitted for non-media files or when measurement fails.
   */
  width?: number
  /**
   * Intrinsic media height measured client-side during upload.
   * Omitted for non-media files or when measurement fails.
   */
  height?: number
  /**
   * Canonical aspect ratio string (e.g. "16:9") snapped from width/height.
   * One of CANONICAL_ASPECT_RATIOS. Omitted when dimensions are unavailable.
   */
  aspectRatio?: string
}

export interface DRMUploadOptions {
  /** Success callback */
  onSuccess?: (result: DRMUploadResult) => void
  /** Error callback */
  onError?: (error: Error | string) => void
  /** Progress callback */
  onProgress?: (progress: number) => void
  /** Status change callback */
  onStatusChange?: (status: string) => void
  /**
   * When true, the upload resolves as soon as the raw file is stored;
   * transcoding continues in the background. When false, the upload
   * waits for transcoding to complete before resolving.
   * @default true
   */
  playRaw?: boolean
}

/**
 * Composable for DRM file uploads with full abstraction
 *
 * @example
 * ```typescript
 * const { uploadToDRM, DRMComponent, isUploading, progress, error } = useDRMUpload({
 *   onSuccess: (result) => console.log('Upload successful:', result),
 *   onError: (error) => console.error('Upload failed:', error)
 * })
 *
 * // In template, pass config props directly to DRM component:
 * <component
 *   :is="DRMComponent"
 *   v-if="DRMComponent && currentDRMFile"
 *   :app-source="'communities'"
 *   :entity-type="'location'"
 *   :entity-id="locationId"
 *   :source-entity-type="'media'"
 *   :source-entity-id="sourceEntityId"
 *   :file="currentDRMFile"
 * />
 *
 * // Usage:
 * const result = await uploadToDRM(file)
 * ```
 */
export const useDRMUpload = (options: DRMUploadOptions) => {
  const REF_READY_RETRY_DELAY_MS = 25
  const REF_READY_MAX_ATTEMPTS = 20

  // Internal state
  const drmFileUploadRef = ref<{
    initiateUpload: () => Promise<void>
    initiateCancelUpload: () => void
  } | null>(null)
  const currentDRMFile = ref<File | null>(null)
  const DRMFileUploadComponent = shallowRef<unknown>(null)
  const drmProgress = ref(0)
  const isDRMUploading = ref(false)
  const drmError = ref<string | null>(null)
  const drmResult = ref<DRMUploadResult | null>(null)

  // Promise resolvers for current upload
  let currentUploadResolve: ((value: { url: string; id: string; licenseId?: string }) => void) | null = null
  let currentUploadReject: ((reason?: Error) => void) | null = null
  // Dimension measurement runs in parallel with the DRM upload. Captured here
  // so `handleDRMSuccess` (invoked by the SDK) can merge the result into the
  // enriched `DRMUploadResult`. Always resolves — failures degrade to `{}`.
  let currentDimsPromise: Promise<RawMediaDimensions> | null = null

  // Computed properties for external use
  const isUploading = computed(() => isDRMUploading.value)
  const progress = computed(() => drmProgress.value)
  const error = computed(() => drmError.value)
  const result = computed(() => drmResult.value)
  // Writable so the parallel-upload pool can override per-file before each upload.
  // Template binding (`slot.instance.playRaw.value`) reactively reflects mutations
  // before the SDK reads the `:play-raw` prop.
  const playRaw = ref<boolean>(options.playRaw ?? true)

  // Event handlers
  const resolveUsableAssetUrl = async (uploadResult: DRMUploadResult): Promise<DRMUploadResult> => {
    const isBrowserUrl = (value?: string) => typeof value === 'string' && /^https?:\/\//.test(value)

    const isVideoAsset = (r: DRMUploadResult) => (r.assetType || '').toLowerCase().includes('video')

    // Video uploads often return an https poster/thumbnail in `url` and the real asset in `processedUrl`.
    // Prefer `processedUrl` when both are http(s) so consumers do not persist the wrong URL.
    if (isVideoAsset(uploadResult) && isBrowserUrl(uploadResult.processedUrl) && isBrowserUrl(uploadResult.url)) {
      return {
        ...uploadResult,
        url: uploadResult.processedUrl,
      }
    }

    if (isBrowserUrl(uploadResult.url)) {
      return uploadResult
    }

    if (isBrowserUrl(uploadResult.processedUrl)) {
      return {
        ...uploadResult,
        url: uploadResult.processedUrl,
      }
    }

    if (!uploadResult.licenseId) {
      return uploadResult
    }

    try {
      const { AssetsDrmService } = await import('../services/assets-drm.service')
      const assetsDrmService = new AssetsDrmService()
      const assetInfo = await assetsDrmService.getAssetInfo(uploadResult.licenseId)
      const hydratedUrl = assetInfo.url || assetInfo.processedUrl
      return {
        ...uploadResult,
        url: hydratedUrl || uploadResult.url,
        processedUrl: uploadResult.processedUrl || assetInfo.processedUrl,
      }
    } catch {
      // Non-fatal: keep licenseId-based result; avoid production console noise (review: §12).
      return uploadResult
    }
  }

  const handleDRMSuccess = async (uploadResult: DRMUploadResult) => {
    // Dimension measurement runs in parallel with the DRM upload. Real file
    // uploads take seconds, so by the time this success handler fires,
    // measurement has almost always resolved (blob-URL loads are effectively
    // instant). The short race here guarantees we never block success on a
    // hung measurement (e.g. jsdom during tests, corrupted media) while still
    // enriching the result whenever measurement is already available.
    const DIMENSION_WAIT_MS = 200
    const dimsPromise: Promise<RawMediaDimensions> = currentDimsPromise
      ? Promise.race([currentDimsPromise, new Promise<RawMediaDimensions>(resolve => setTimeout(() => resolve({}), DIMENSION_WAIT_MS))])
      : Promise.resolve<RawMediaDimensions>({})

    const [resolvedResult, dims] = await Promise.all([resolveUsableAssetUrl(uploadResult), dimsPromise])
    currentDimsPromise = null

    const normalizedUploadResult: DRMUploadResult = {
      ...resolvedResult,
      ...buildMediaDimensionFields(dims),
    }

    // Update internal state
    drmResult.value = normalizedUploadResult
    isDRMUploading.value = false
    drmError.value = null
    drmProgress.value = 100

    if (options.onSuccess) {
      options.onSuccess(normalizedUploadResult)
    }

    // Resolve the Promise for upload function
    if (currentUploadResolve) {
      // Prefer browser-usable URL for persisted UI fields.
      const displayUrl = normalizedUploadResult.url || normalizedUploadResult.processedUrl || normalizedUploadResult.licenseId
      currentUploadResolve({
        url: displayUrl,
        id: normalizedUploadResult.licenseId,
        licenseId: normalizedUploadResult.licenseId,
      })
      currentUploadResolve = null
      currentUploadReject = null
    }

    // Clear current file
    currentDRMFile.value = null
  }

  const handleDRMError = (uploadError: Error | string | undefined) => {
    const errorMessage =
      typeof uploadError === 'string' ? uploadError : uploadError instanceof Error ? uploadError.message : 'DRM upload failed'
    console.error('DRM upload error:', errorMessage)

    // Discard any in-flight dimension measurement for this attempt.
    currentDimsPromise = null

    // Update internal state
    isDRMUploading.value = false
    drmError.value = errorMessage
    drmProgress.value = 0

    // Call external error callback
    if (options.onError) {
      options.onError(uploadError)
    }

    // Reject the Promise for upload function
    if (currentUploadReject) {
      currentUploadReject(new Error(errorMessage))
      currentUploadResolve = null
      currentUploadReject = null
    }

    // Clear current file
    currentDRMFile.value = null
  }

  const handleDRMProgress = (newProgress: number) => {
    drmProgress.value = newProgress

    // Call external progress callback
    if (options.onProgress) {
      options.onProgress(newProgress)
    }
  }

  const handleDRMStatusChange = (status: string) => {
    // Call external status change callback
    if (options.onStatusChange) {
      options.onStatusChange(status)
    }
  }

  /**
   * Upload a file using DRM
   *
   * @param file - File to upload
   * @returns Promise that resolves with upload result
   */
  const waitForDRMRef = async () => {
    for (let attempt = 0; attempt < REF_READY_MAX_ATTEMPTS; attempt++) {
      await nextTick()
      if (drmFileUploadRef.value) {
        return true
      }
      await new Promise(resolve => setTimeout(resolve, REF_READY_RETRY_DELAY_MS))
    }
    return false
  }

  const uploadToDRM = async (file: File): Promise<{ url: string; id: string; licenseId?: string }> => {
    // Only run on client side
    if (process.server) {
      throw new Error('DRM upload only available on client side')
    }

    return new Promise(async (resolve, reject) => {
      try {
        // Store resolve/reject for the event handlers to use
        currentUploadResolve = resolve
        currentUploadReject = reject

        // Reset state
        drmError.value = null
        drmResult.value = null
        drmProgress.value = 0

        // Kick off dimension measurement in parallel with the DRM upload so we
        // do not block the upload. The utility never throws — a failed
        // measurement simply resolves to `{}` and the enriched result omits
        // the dimension fields.
        currentDimsPromise = getMediaDimensionsFromFile(file)

        // Dynamically import DRM FileUpload component (client-side only)
        // Must be dynamic import because the library accesses 'document' at import time
        if (!DRMFileUploadComponent.value) {
          const { FileUpload } = await import('@ghl-revex/assets-drm-client')
          DRMFileUploadComponent.value = markRaw(FileUpload)
        }

        currentDRMFile.value = file

        const isRefReady = await waitForDRMRef()

        // Start upload — the DRM FileUpload component handles its own
        // error/timeout lifecycle (XHR errors, processing failures, 4-hour
        // processing timeout) and calls onError/onSuccess props accordingly.
        if (isRefReady && drmFileUploadRef.value) {
          isDRMUploading.value = true
          await drmFileUploadRef.value.initiateUpload()
        } else {
          console.error('❌ useDRMUpload: DRM FileUpload component not ready')
          reject(new Error('DRM FileUpload component not ready'))
        }
      } catch (uploadError) {
        isDRMUploading.value = false
        console.error('❌ useDRMUpload: Error in uploadToDRM:', uploadError)
        reject(uploadError instanceof Error ? uploadError : new Error('Failed to initialize DRM upload'))
      }
    })
  }

  /**
   * Cancel the current upload
   */
  const cancelUpload = async () => {
    if (drmFileUploadRef.value && isDRMUploading.value) {
      try {
        drmFileUploadRef.value.initiateCancelUpload()
        isDRMUploading.value = false
        drmProgress.value = 0
        currentDRMFile.value = null
        currentDimsPromise = null

        // Reject current promise if exists
        if (currentUploadReject) {
          currentUploadReject(new Error('Upload cancelled'))
          currentUploadResolve = null
          currentUploadReject = null
        }
      } catch (error) {
        console.error('Failed to cancel upload:', error)
      }
    }
  }

  /**
   * Reset the upload state
   */
  const resetState = () => {
    isDRMUploading.value = false
    drmProgress.value = 0
    drmError.value = null
    drmResult.value = null
    currentDRMFile.value = null
    currentUploadResolve = null
    currentUploadReject = null
    currentDimsPromise = null
  }

  // Expose component and file refs for template usage
  const currentFile = computed(() => currentDRMFile.value)
  const componentRef = computed(() => DRMFileUploadComponent.value)

  return {
    // Main upload function
    uploadToDRM,

    // Template refs
    currentFile,
    componentRef,
    drmFileUploadRef,

    // Event handlers for template
    handleDRMSuccess,
    handleDRMError,
    handleDRMProgress,
    handleDRMStatusChange,

    // Reactive state
    isUploading,
    progress,
    error,
    result,
    playRaw,

    // Utility functions
    cancelUpload,
    resetState,
  }
}
